import CashMachine from 'assets/sounds/cash-machine.mp3'
import { actions, actionTypes, selectors } from 'data'
import { notifyError, notifySuccess } from 'data/App/sagas'
import { OrderMessageType, OrderType } from 'data/types'
import { call, put, select, take, takeEvery } from 'redux-saga/effects'
import webSocket from 'services/api/webSocket'
import { getClOrdID, isCompact, sanitizeMessage } from 'utils'
import { addTiming } from 'utils/datadog'

import {
  LiveOrderType,
  Position,
  RejectedOrderMessageType,
  SnapshotOrderMessageType,
  TradingSubscribedMessage,
  UpdateOrderMessageType
} from './types'

const playSound = () => {
  try {
    const audio = new Audio(CashMachine)
    audio.play()
  } catch (e) {}
}

const playSoundIfEnabled = function* () {
  const isSoundEnabled = yield select(selectors.auth.localSettings.getOrderFilledSoundSetting)
  if (isSoundEnabled) yield call(playSound)
}

const setAlert = function* (message: OrderType) {
  const showNotifications = yield select(selectors.auth.getShowNotifications)

  const humanizedOrderType =
    message.ordType.charAt(0).toUpperCase() + message.ordType.slice(1).toLowerCase()

  const [baseCurrency, quoteCurrency] = message.symbol.split('-')

  const formattedSide = message.side.toUpperCase()

  let humanizedOrderDescription = `${humanizedOrderType} ${formattedSide} ${message.orderQty} ${baseCurrency}`

  function isLimitOrder(message: OrderType) {
    return [webSocket.order.type.LIMIT, webSocket.order.type.STOP_LIMIT].includes(message.ordType)
  }

  if (isLimitOrder(message)) {
    humanizedOrderDescription += ` for ${message.price} ${quoteCurrency}`
  }

  switch (message.ordStatus) {
    case webSocket.order.status.OPEN: {
      if (!showNotifications) {
        return
      }

      yield call(notifySuccess, 'OK_ORDER_CREATED', 5, humanizedOrderDescription)
      break
    }
    case webSocket.order.status.CANCELLED:
      switch (message.text) {
        case '':
          if (!showNotifications) {
            return
          }

          yield call(notifySuccess, 'KO_ORDER_CANCELLED', 5, humanizedOrderDescription)
          break
        case 'Canceled by User':
          if (!showNotifications) {
            return
          }

          yield call(notifySuccess, 'KO_ORDER_CANCELLED', 5, humanizedOrderDescription)
          break
        case 'Canceled, no market for market order':
          let subtext

          if (message.cumQty === 0) {
            subtext = 'Not enough liquidity for a market order'
          } else {
            const instruments = yield select(selectors.instrument.getInstrumentsIndexed)

            const instrument = instruments[message.symbol]

            const formattedAvgPx = message.avgPx?.toFixed(instrument.counter_currency_scale)

            subtext = `Partially filled ${message.cumQty} ${baseCurrency} at ${formattedAvgPx} ${quoteCurrency}`
          }

          yield call(notifyError, humanizedOrderDescription, 'KO_ORDER_CANCELLED', subtext)
          break
        case 'Met cash limit':
          yield call(
            notifyError,
            humanizedOrderDescription,
            'KO_ORDER_CANCELLED',
            'Insufficient balance'
          )
          break
        case 'STP cancel new':
          yield call(
            notifyError,
            humanizedOrderDescription,
            'KO_ORDER_CANCELLED',
            'Cancelled due to self trade prevention'
          )
          break
        case 'Bad stop price on stop':
          yield call(
            notifyError,
            humanizedOrderDescription,
            'KO_ORDER_CANCELLED',
            'Stop price out of limit'
          )
          break
        default:
          yield call(notifyError, humanizedOrderDescription, 'KO_ORDER_CANCELLED', message.text)
          break
      }

      break
    case webSocket.order.status.FILLED: {
      if (!showNotifications) {
        return
      }

      if (isLimitOrder(message)) {
        yield call(notifySuccess, 'OK_ORDER_FILLED', undefined, humanizedOrderDescription)
      } else {
        const instruments = yield select(selectors.instrument.getInstrumentsIndexed)

        const instrument = instruments[message.symbol]

        const formattedAvgPx = message.avgPx?.toFixed(instrument.counter_currency_scale)

        yield call(
          notifySuccess,
          'OK_ORDER_FILLED',
          undefined,
          humanizedOrderDescription,
          undefined,
          `Average fill price at ${formattedAvgPx} ${quoteCurrency}`
        )
      }
      const lookback = yield select(selectors.historical.getLookback)
      yield put(actions.historical.getFilledOrders(lookback))
      yield put(actions.historical.getFilledTrades(lookback))
      yield call(playSoundIfEnabled)
      break
    }
    case webSocket.order.status.PARTIAL: {
      if (!showNotifications) {
        return
      }

      if (isLimitOrder(message)) {
        yield call(
          notifySuccess,
          'OK_ORDER_PARTIALLY_FILLED',
          undefined,
          humanizedOrderDescription,
          undefined,
          `Executed ${message.lastShares} ${baseCurrency}`
        )
      }
      const lookback = yield select(selectors.historical.getLookback)
      yield put(actions.historical.getFilledOrders(lookback))
      yield put(actions.historical.getFilledTrades(lookback))
      yield call(playSoundIfEnabled)
      break
    }
    case webSocket.order.status.PENDING: {
      if (!showNotifications) {
        return
      }

      if ([webSocket.order.type.STOP, webSocket.order.type.STOP_LIMIT].includes(message.ordType)) {
        yield call(
          notifySuccess,
          'OK_ORDER_PENDING',
          5,
          humanizedOrderDescription,
          undefined,
          `Trigger at ${message.stopPx} ${quoteCurrency}`
        )
      } else {
        yield call(notifySuccess, 'OK_ORDER_PENDING', 5, humanizedOrderDescription, undefined)
      }
      break
    }
    case webSocket.order.status.REJECTED:
      yield call(notifyError, humanizedOrderDescription, 'KO_ORDER_CANCELLED', message.text)
      break
    case webSocket.order.status.EXPIRED:
      if ([webSocket.order.tif.FOK, webSocket.order.tif.IOC].includes(message.timeInForce)) {
        if (message.cumQty === 0) {
          yield call(
            notifyError,
            humanizedOrderDescription,
            'KO_ORDER_EXPIRED',
            `Could not match ${message.timeInForce}`
          )
        } else {
          yield call(
            notifyError,
            humanizedOrderDescription,
            'KO_ORDER_EXPIRED',
            `Partially filled ${message.timeInForce}: ${message.cumQty} ${baseCurrency} at ${message.avgPx} ${quoteCurrency}`
          )
        }
      } else {
        yield call(notifyError, humanizedOrderDescription, 'KO_ORDER_EXPIRED', message.text)
      }

      break
    default: {
    }
  }
}

const messageManager = function* (action) {
  const message: OrderMessageType = action.payload

  if (message.event === webSocket.event.REJECTED && message.text === 'Already subscribed') {
    return
  }

  // From now on messages are published with `messageReceived` so anyone can handle them. Leaving setRejected, setSnapshot, setUpdate as legacy code
  yield put(actions.order.messageReceived(message))
  if (message.action === webSocket.action.POSTIION_MARGIN_DETAILS) return

  if (message.event === webSocket.event.REJECTED) {
    yield put(actions.order.setRejected(message as RejectedOrderMessageType))
    return
  }

  if (message.event === webSocket.event.SNAPSHOT) {
    const snapshot = message as SnapshotOrderMessageType
    yield put(actions.order.setSnapshot(snapshot.orders, snapshot.positions ?? []))
    return
  }

  if (message.event === webSocket.event.UPDATED) {
    yield put(actions.order.setUpdate(sanitizeMessage(message) as LiveOrderType))
    yield call(setAlert, message as UpdateOrderMessageType)
    return
  }

  if (message.event === webSocket.event.SUBSCRIBED) {
    yield put(actions.order.setSubscribed())
    const action = message as TradingSubscribedMessage
    yield put(
      actions.margin.setMarginMetadata({
        marginEligible: action.marginEligible,
        marginRejectionReason: action.marginRejectionReason,
        signedMarginUserAgreement: action.signedMarginUserAgreement
      })
    )
    return
  }

  if (message.event === webSocket.event.UNSUBSCRIBED) {
    yield put(actions.order.reset())
  }
}

const lastPlacedOrderTimeTracker = function (action) {
  const message: OrderMessageType = action.payload
  const lastPlaced = localStorage.getItem('last-order-placed')
  if (message.event === webSocket.event.UPDATED && lastPlaced === message.clOrdID) {
    addTiming('place-order-time-end')
    localStorage.removeItem('last-order-placed')
  }
}

const cancel = function* (action: ReturnType<typeof cancel>) {
  if (typeof action.payload.id === 'string') {
    yield put(
      actions.socket.send({
        action: webSocket.action.CANCEL,
        channel: webSocket.channel.TRADING,
        orderID: action.payload.id
      })
    )
  }

  if (Array.isArray(action.payload.id)) {
    for (let i = 0; i < Object.keys(action.payload.id).length; i += 1) {
      const id = action.payload.id[i]
      yield put(
        actions.socket.send({
          action: webSocket.action.CANCEL,
          channel: webSocket.channel.TRADING,
          orderID: id
        })
      )
    }
  }
}

const start = function* () {
  const channel = webSocket.channel.TRADING
  yield put(actions.socket.subscribe({ channel }))
}

const stop = function* () {
  yield put(actions.socket.unsubscribe({ channel: webSocket.channel.TRADING }))
}

const fetchMarginCallDetails = function* (action: ReturnType<typeof fetchMarginCallDetails>) {
  const requestId = `MarginCall-${+new Date()}`
  yield put(
    actions.socket.send({
      action: webSocket.action.MARGIN_CALL_DETAILS,
      channel: webSocket.channel.TRADING,
      positionId: action.payload.positionId,
      requestId
    })
  )

  const responseAction = yield take(
    (action) =>
      action.type === actionTypes.order.MESSAGE_RECEIVED && action.payload.requestId === requestId
  )

  if (responseAction.payload.event === webSocket.event.UPDATED) {
    yield put(actions.order.setMarginCallDetails(responseAction.payload))
  }
}

// FIXME this and fetchMarginCallDetails have very similar code
const fetchMarginOrderDetails = function* (action: ReturnType<typeof fetchMarginOrderDetails>) {
  const authed = yield select(selectors.auth.isAuthenticated)
  if (!authed) return

  const requestId = `MarginDetails-${+new Date()}`
  yield put(
    actions.socket.send({
      action: webSocket.action.POSTIION_MARGIN_DETAILS,
      channel: webSocket.channel.TRADING,
      requestId,
      ...action.payload
    })
  )

  const responseAction = yield take(
    (action) =>
      action.type === actionTypes.order.MESSAGE_RECEIVED && action.payload.requestId === requestId
  )

  if (responseAction.payload.event === webSocket.event.UPDATED) {
    yield put(actions.order.setMarginDetails(responseAction.payload))
  }
}

const closeMarginPosition = function* (action) {
  const screenWidth = yield select(selectors.app.getScreenWidth)
  yield put(
    actions.socket.send({
      action: webSocket.action.CLOSE_MARGIN_ORDER,
      channel: webSocket.channel.TRADING,
      timeInForce: webSocket.order.tif.GTC,
      ...action.payload,
      clOrdID: getClOrdID(isCompact(screenWidth))
    })
  )
}

const adjustMarginPosition = function* (action) {
  yield put(
    actions.socket.send({
      action: webSocket.action.ADJUST_MARGIN_ORDER,
      channel: webSocket.channel.TRADING,
      ...action.payload
    })
  )
}

const updatePosition = function* (action) {
  const message = action.payload
  yield put(actions.order.updatePosition(sanitizeMessage(message) as Position))
}

const showRejectMessage = function* (action) {
  const message = action.payload
  if (message.action === webSocket.action.POSTIION_MARGIN_DETAILS) return
  yield call(notifyError, message.text, 'KO_GENERIC')
}

export default function* orderChannel() {
  yield takeEvery(actionTypes.auth.SET_SOCKET_AUTHED, start)
  yield takeEvery(actionTypes.order.CANCEL, cancel)
  yield takeEvery(actionTypes.order.STOP, stop)
  yield takeEvery(actionTypes.auth.SIGNOUT, function* () {
    yield put(actions.order.reset())
  })
  yield takeEvery(actionTypes.order.FETCH_MARGIN_CALL_DETAILS, fetchMarginCallDetails)
  yield takeEvery(actionTypes.order.FETCH_MARGIN_ORDER_DETAILS, fetchMarginOrderDetails)
  yield takeEvery(actionTypes.order.CLOSE_MARGIN_POSITION, closeMarginPosition)
  yield takeEvery(actionTypes.order.ADJUST_MARGIN_POSITION, adjustMarginPosition)
  yield takeEvery(
    (action) =>
      action.type === actionTypes.order.MESSAGE_RECEIVED &&
      action.payload.channel === webSocket.channel.TRADING &&
      action.payload.event === webSocket.event.POSITION_UPDATED,
    updatePosition
  )
  yield takeEvery(
    (action) =>
      action.type === actionTypes.order.MESSAGE_RECEIVED &&
      action.payload.event === webSocket.event.REJECTED,
    showRejectMessage
  )
  const channel = webSocket.channel.TRADING
  yield takeEvery(
    (action) => action.type === actionTypes.socket.RECEIVED && action.payload.channel === channel,
    messageManager
  )
  yield takeEvery(
    (action) => action.type === actionTypes.socket.RECEIVED && action.payload.channel === channel,
    lastPlacedOrderTimeTracker
  )
}
